import { useState, useEffect } from 'react';
import { X, Image } from 'lucide-react';
import { Folder } from './types';

interface FolderFormProps {
  onClose: () => void;
  onSubmit: (name: string, imgUrl: string, priority: number) => void;
  // parentId removed as it doesn't exist in the database
  editingFolder: Folder | null;
  folders: Folder[];
}

const FolderForm = ({ 
  onClose, 
  onSubmit,
  // parentId parameter removed
  editingFolder,
  folders 
}: FolderFormProps) => {
  const [folderName, setFolderName] = useState('');
  // Parent ID state removed as it doesn't exist in the database
  const [imgUrl, setImgUrl] = useState('');
  const [priority, setPriority] = useState<number>(0);

  useEffect(() => {
    if (editingFolder) {
      setFolderName(editingFolder.name);
      // parent_id reference removed
      setImgUrl(editingFolder.img_url || '');
      setPriority(editingFolder.priority || 0);
    }
  }, [editingFolder]);

  const handleSubmit = () => {
    if (folderName.trim()) {
      console.log('FolderForm submitting with:', { 
        folderName, 
        imgUrl: imgUrl.trim(), 
        priority, 
        isEditing: !!editingFolder 
      });
      onSubmit(folderName, imgUrl.trim(), priority);
    }
  };

  // Parent folder functionality removed as parent_id doesn't exist in the database

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">
            {editingFolder ? 'Edit Folder' : 'Add New Folder'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Folder Name
            </label>
            <input
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Enter folder name"
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
            />
          </div>
          
          {/* Parent Folder selection removed as parent_id doesn't exist in the database */}
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Display Priority (Lower numbers appear first)
            </label>
            <input
              type="number"
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
              placeholder="0"
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
            />
            <p className="text-xs text-gray-400 mt-1">
              Controls the order folders appear in the list. Lower numbers appear first.
            </p>
          </div>
          
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-1">
              <Image className="w-4 h-4" />
              Image URL (Optional)
            </label>
            <input
              type="text"
              value={imgUrl}
              onChange={(e) => setImgUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
            />
            {imgUrl && (
              <div className="mt-2 flex items-center">
                <div className="w-10 h-10 overflow-hidden rounded-md border border-gray-600 mr-2">
                  <img 
                    src={imgUrl} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/100?text=Error';
                    }}
                  />
                </div>
                <span className="text-xs text-gray-400">Image preview</span>
              </div>
            )}
          </div>
          
          <button
            onClick={handleSubmit}
            disabled={!folderName.trim()}
            className="w-full bg-[#c9fffc] text-gray-900 rounded-lg px-4 py-2 font-medium hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] disabled:opacity-50"
          >
            {editingFolder ? 'Save Changes' : 'Add Folder'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FolderForm;
